import React, { useCallback, useState } from 'react';

import { useKitTranslate } from 'common/components/kit-shared/useKitTranslate';
import { showToast } from 'utils/toasts';
import { ToastTypes } from 'common/types/toasts';
import { track } from 'utils/kitAnalytics';
import { useKitWorkspace } from 'containers/KitWorkspaceStorage/context';
import { deleteAccessory, patchAccessory } from 'containers/KitWorkspaceStorage/api';
import type { IKitAccessory } from 'containers/KitWorkspaceStorage/types';

import QtyStepper from 'common/components/kit-shared/QtyStepper';
import styles from './styles.module.scss';

interface IAccessoryLinesProps {
  accessories: IKitAccessory[];
}

/**
 * Loose accessory lines (companions) attached to a kit or standalone assembly.
 * Qty stepper (patchAccessory) + remove (deleteAccessory).
 */
const AccessoryLines = ({ accessories }: IAccessoryLinesProps) => {
  const t = useKitTranslate();
  const { workspaceKey, refetch } = useKitWorkspace();
  const [busy, setBusy] = useState<string | null>(null);

  const onQty = useCallback(async (acc: IKitAccessory, qty: number) => {
    setBusy(acc.uuid);
    try {
      await patchAccessory(acc.uuid, { qty });
      track('kit_line_qty_changed', { workspaceKey: workspaceKey ?? undefined, accessoryId: acc.uuid, qty });
      await refetch();
    } catch {
      showToast(ToastTypes.error, t('texts.kits.toast.updateError'));
    } finally {
      setBusy(null);
    }
  }, [workspaceKey, refetch, t]);

  const onRemove = useCallback(async (acc: IKitAccessory) => {
    setBusy(acc.uuid);
    try {
      await deleteAccessory(acc.uuid);
      showToast(ToastTypes.success, t('texts.kits.toast.partRemoved'));
      await refetch();
    } catch {
      showToast(ToastTypes.error, t('texts.kits.toast.updateError'));
    } finally {
      setBusy(null);
    }
  }, [refetch, t]);

  if (!accessories.length) return null;

  return (
    <ul className={styles.lineList}>
      {accessories.map((acc) => (
        <li key={acc.uuid} className={styles.line}>
          <div className={styles.lineInfo}>
            <span className={styles.linePartNumber}>{acc.partNumber}</span>
            {acc.source === 'custom' && <span className={styles.pill}>{t('texts.kits.pills.custom')}</span>}
            {acc.description && <span className={styles.lineSub}>{acc.description}</span>}
          </div>
          <div className={styles.lineActions}>
            <QtyStepper value={acc.qty} onChange={(n) => onQty(acc, n)} disabled={busy === acc.uuid} />
            <button
              type="button"
              className={styles.removeBtn}
              onClick={() => onRemove(acc)}
              disabled={busy === acc.uuid}
              aria-label={t('texts.kits.removePart')}
            >
              &#x2715;
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
};

export default AccessoryLines;
